import type { NonUndefined, ValuesType } from 'utility-types'
import React, { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react'
import { type FormatListOptions, FormattedList, FormattedMessage, FormattedRelativeTime, IntlProvider, type IntlShape, useIntl } from 'react-intl'
import type { Props as ReactIntlTranslationProps } from 'react-intl/src/components/message'

import type { ReadonlyDate, ReadonlyRecord } from '../../../utils'

import { Translation } from '../../../domain'

export type TranslationProviderProps = Readonly<{
    internationalization: Pick<IntlShape, 'locale' | 'messages' | 'onError'>
}>

export const TranslationProvider: FC<PropsWithChildren<TranslationProviderProps>> = ({ internationalization, children }) => {
    const { locale, messages, onError } = internationalization

    return (
        <IntlProvider locale={locale} messages={messages} onError={onError}>
            {children}
        </IntlProvider>
    )
}

/**
 * There is type mismatch between translation values that 'react-intl' expects (ReactIntlTranslationValues)
 * and the values we use across the project (CybusTranslationValues).
 * Currently, the only difference is Date vs. ReadonlyDate.
 *
 * The types below shall not be exported,
 * all type assertions of kind `cybusTranslationValues as ReactIntlTranslationValues` should be made in this file.
 */
type ReactIntlTranslationValues = Readonly<ReactIntlTranslationProps['values']>
type CybusTranslationValues = ReadonlyRecord<string, Exclude<ValuesType<NonUndefined<ReactIntlTranslationValues>>, Date> | ReadonlyDate>

export type FormatTranslationProps = Readonly<{
    /**
     * The targeted translation
     */
    id: Translation
    /**
     * The values to the translation
     */
    values?: CybusTranslationValues
}>

type TranslatorHook = {
    formatTranslation(id: FormatTranslationProps['id'], values?: FormatTranslationProps['values']): string
    formatList(values: readonly ReactNode[], options?: FormatListOptions): string
    /**
     * A countdown to `value` in seconds, minutes, hours or days, rounded up so it never shows less time than is left (4:01 left
     * reads "in 5 minutes"), with each unit counting down to its full size before the next smaller one takes over ("in
     * 2 minutes", then "in 60 seconds"), as a plain string for contexts that can't render JSX (e.g. `InlineAlert`'s
     * string-only content)
     */
    formatTimeLeft(value: ReadonlyDate): string
}

const COUNTDOWN_UNITS = [
    { unit: 'second', seconds: 1, upTo: 60 },
    { unit: 'minute', seconds: 60, upTo: 60 },
    { unit: 'hour', seconds: 3_600, upTo: 24 },
] as const

const SECONDS_PER_DAY = 86_400

const toCountdown = (seconds: number): Readonly<{ value: number, unit: (typeof COUNTDOWN_UNITS)[number]['unit'] | 'day' }> => {
    for (const { unit, seconds: unitSeconds, upTo } of COUNTDOWN_UNITS) {
        const value = Math.ceil(seconds / unitSeconds)
        if (Math.abs(value) <= upTo) {
            return { value, unit }
        }
    }

    return { value: Math.ceil(seconds / SECONDS_PER_DAY), unit: 'day' }
}

export const useTranslator = (): TranslatorHook => {
    const intl = useIntl()

    return useMemo(
        () => ({
            formatTranslation: (id, values) => intl.formatMessage({ id }, values as ReactIntlTranslationValues) as string,
            formatList: (values, options) => intl.formatList(values, options) as string,
            formatTimeLeft: (value) => {
                const { value: relativeValue, unit } = toCountdown((value.getTime() - Date.now()) / 1_000)
                return intl.formatRelativeTime(relativeValue, unit)
            },
        }),
        [intl]
    )
}

export const FormattedTranslation: FC<FormatTranslationProps> = ({ id, values }: FormatTranslationProps) => (
    <FormattedMessage id={id} values={values as ReactIntlTranslationValues} />
)

export const FormattedTimePast: FC<Readonly<{ value: ReadonlyDate }>> = ({ value }) => (
    <FormattedRelativeTime unit="second" value={(value.getTime() - Date.now()) / 1_000} updateIntervalInSeconds={1} />
)

/**
 * Use this component to render dates
 * Formatting options are:
 *  - Datetime: both date and time are displayed
 *  - Time Only: only time is displayed
 *  - Date Only: only date is displayed
 */
export const FormattedDateTime: FC<Readonly<{ date: ReadonlyDate, format: 'datetime' | 'dateOnly' | 'timeOnly' | 'dateRangeChip' }>> = ({ format, date }) => (
    <FormattedTranslation id={Translation.DATETIME} values={{ type: format, date }} />
)

export { FormattedList }
